// @givmo/app-charity — Firebase singletons (Auth + App Check) for the SPA.
//
// Initialized ONCE at app boot from the env subset. The charity portal runs in the browser, so the
// live Firebase client SDK is the auth + App Check source: Bearer ID tokens come from the signed-in
// `firebase/auth` session, and App Check tokens from the reCAPTCHA Enterprise provider. All config is
// env-injected via @givmo/auth (no hardcoded credentials). Mirrors the admin app's firebase runtime.

import { initFirebaseApp, getFirebaseAuth, initGivmoAppCheck } from '@givmo/auth';
import type { Auth } from 'firebase/auth';
import type { AppCheck } from 'firebase/app-check';
import type { CharityEnv } from './env';

export interface FirebaseRuntime {
  auth: Auth;
  /** `null` when no reCAPTCHA Enterprise site key is configured (local/dev without App Check). */
  appCheck: AppCheck | null;
}

let runtime: FirebaseRuntime | null = null;

/**
 * Initialize (or reuse) the Firebase Auth + App Check singletons from env. Idempotent: a second call
 * returns the already-initialized runtime (the underlying @givmo/auth init helpers are idempotent
 * too, so HMR / double-import is safe).
 *
 * App Check is stood up only when a site key is present; without one (local/dev) it is `null` and the
 * api-client middleware simply omits the `X-Firebase-AppCheck` header — the backend bypasses App
 * Check in local/dev, so attested calls still succeed there.
 */
export function initFirebaseRuntime(env: CharityEnv): FirebaseRuntime {
  if (runtime !== null) return runtime;

  const app = initFirebaseApp(env.firebase);
  const auth = getFirebaseAuth(app);

  let appCheck: AppCheck | null = null;
  if (env.recaptchaEnterpriseSiteKey.length > 0) {
    appCheck = initGivmoAppCheck(app, {
      reCaptchaEnterpriseSiteKey: env.recaptchaEnterpriseSiteKey,
      debugToken: env.appCheckDebugToken,
    });
  }

  runtime = { auth, appCheck };
  return runtime;
}

/** Reset the cached runtime — test-only seam so each test inits fresh. */
export function resetFirebaseRuntimeForTests(): void {
  runtime = null;
}
